import React, {useEffect, useState, useRef} from 'react';
import {useNavigate, useSearchParams} from 'react-router-dom';
import {StorageService} from "../../infrastructure/storage/storage-service";
import {useAuthStore} from "../stores/auth-store";
import {useUserStore} from "../stores/user-store";
import {InvalidTokenError} from "jwt-decode";
import {AuthApiService} from "../../infrastructure/api/auth-api-service";
import {invitationApi} from "../../infrastructure/api/company-staff-api-service";
import {isWorkspaceSelection, type WorkspaceSelectionResponse} from "../../domain/types/auth";
import {PENDING_INVITATION_TOKEN_KEY} from "../../domain/types/staff";
import {resolveErrorMessage} from "../../shared/utils/save-flow";
import {WorkspacePickerDialog, workspaceHomePath} from "../components/Auth/WorkspacePickerDialog";

export const AuthSuccessPage: React.FC = () => {
  const storageService = new StorageService();
  const authApiService = new AuthApiService();

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const {socialLogin, setLoading, isLoading, completeLogin} = useAuthStore();
  const [workspaceSelection, setWorkspaceSelection] = useState<WorkspaceSelectionResponse | null>(null);
  const {fetchProfile, profile} = useUserStore();

  const hasProcessedRef = useRef(false); // 중복 실행 방지를 위한 플래그

  useEffect(() => {
    (async () => {
      // 중복 실행 방지
      if (hasProcessedRef.current) {
        console.log('Social login already processed, skipping...');
        return;
      }
      hasProcessedRef.current = true;

      setLoading(true);

      const id = searchParams.get("id");
      const token = searchParams.get("token");

      try {
        // 토큰이 존재하지 않을 경우 실패 페이지로 이동합니다.
        if (!token) {
          throw new InvalidTokenError("요청에 토큰이 포함되어 있지 않습니다.");
        }

        // 초대 수락 화면에서 소셜 로그인을 시작했다면 일반 로그인 대신 초대 수락으로 이어 간다
        const invitationToken = sessionStorage.getItem(PENDING_INVITATION_TOKEN_KEY);
        if (invitationToken) {
          sessionStorage.removeItem(PENDING_INVITATION_TOKEN_KEY);
          try {
            const accepted = await invitationApi.acceptWithSocial(invitationToken, {
              providerUserToken: token,
              providerUserId: id + "",
            });
            await completeLogin(accepted);
            navigate(accepted.companyId ? `/company/${accepted.companyId}` : "/");
          } catch (e) {
            navigate(`/auth/invitation?token=${encodeURIComponent(invitationToken)}`, {
              state: {error: resolveErrorMessage(e, "초대를 수락하지 못했습니다.")},
            });
          }
          return;
        }

        const {data} = await authApiService.socialLogin({
          providerUserToken: token,
          providerUserId: id + "",
          companyId: undefined
        })

        // 여러 회사에 속한 계정: 들어갈 회사를 고르게 한다
        if (isWorkspaceSelection(data)) {
          setLoading(false);
          setWorkspaceSelection(data);
          return;
        }

        // 로컬스토리지 설정
        if (data?.accessToken && data?.refreshToken) {
          storageService.setItem("accessToken", data.accessToken);
          storageService.setItem("refreshToken", data.refreshToken);
        } else {
          throw new Error("토큰이 유효하지 않거나 토큰을 수신하지 못함.");
        }

        await socialLogin(token, id + "", data.companyId ?? undefined);
        await fetchProfile(data.userId);
      } catch (e: unknown) {
        if (e instanceof Error) {
          alert(e.message);
        }
        navigate("/auth/failed");
        return;
      }
    })();
  }, [authApiService, completeLogin, fetchProfile, navigate, profile, profile?.name, searchParams, setLoading, socialLogin, storageService]);

  useEffect(() => {
    if (profile === null) return;

    if (!profile.name) {
      navigate("/signup/social");
    } else {
      navigate("/");
    }
  }, [profile, navigate]);

  if (workspaceSelection) {
    return (
        <div className="min-h-screen bg-gray-50">
          <WorkspacePickerDialog
              selection={workspaceSelection}
              onClose={() => navigate("/")}
              onCompleted={(_, workspace) => navigate(workspaceHomePath(workspace))}
          />
        </div>
    );
  }

  return (
      <div className="flex items-center justify-center min-h-screen">\
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">로그인 처리 중...</p>
          <p className="mt-2 text-sm text-gray-500">잠시만 기다려주세요.</p>
        </div>
      </div>
  )
}
